import React, { useState, useEffect } from 'react';
import './VisitorCounter.css';
import { IconGlobe, IconTrending } from './Icons';
import { API_URL } from '../config/api';
import { fetchWithCache } from '../utils/apiCache';

// Shared singleton telemetry state & fetch manager
// Ensures even if multiple VisitorCounter instances are mounted (desktop + mobile drawer),
// only ONE HTTP request is dispatched, with a strict 4s timeout so it NEVER stalls browser connections.
let globalStats = (() => {
  try {
    const cached = localStorage.getItem('lysarq_total_visitors');
    if (cached) return JSON.parse(cached);
  } catch {}
  return { total_visits: 0, today_visits: 0, active_now: 1 };
})();

const listeners = new Set();
let activeFetchPromise = null;
let lastSyncTime = 0;
const SYNC_THROTTLE_MS = 10000;

function broadcastStats(newStats) {
  globalStats = newStats;
  try {
    localStorage.setItem('lysarq_total_visitors', JSON.stringify(newStats));
  } catch {}
  listeners.forEach((fn) => {
    try { fn(newStats); } catch {}
  });
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 4000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const data = await fetchWithCache(url, { ...options, signal: controller.signal }, 10000);
    return data;
  } finally {
    clearTimeout(timer);
  }
}

async function syncGlobalVisitorStats() {
  const now = Date.now();
  if (activeFetchPromise) {
    return activeFetchPromise;
  }
  if (now - lastSyncTime < SYNC_THROTTLE_MS && globalStats.total_visits > 0) {
    return globalStats;
  }

  activeFetchPromise = (async () => {
    try {
      const sessionVisited = sessionStorage.getItem('lysarq_session_visit');
      let data;
      if (!sessionVisited) {
        data = await fetchWithTimeout(`${API_URL}/telemetry/visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }, 4000);
        sessionStorage.setItem('lysarq_session_visit', '1');
      } else {
        data = await fetchWithTimeout(`${API_URL}/telemetry/stats`, {}, 4000);
      }

      if (data && typeof data.total_visits === 'number') {
        broadcastStats(data);
        lastSyncTime = Date.now();
        return data;
      }
    } catch (err) {
      console.debug('Telemetry service standby:', err.name === 'AbortError' ? 'timeout (4s)' : err.message);
    } finally {
      activeFetchPromise = null;
    }
    return globalStats;
  })();

  return activeFetchPromise;
}

export const VisitorCounter = ({ lang = 'en' }) => {
  const [stats, setStats] = useState(globalStats);
  const totalVisits = stats?.total_visits ?? 0;
  const todayVisits = stats?.today_visits ?? 0;

  useEffect(() => {
    listeners.add(setStats);
    syncGlobalVisitorStats();

    // Periodic 60s background refresh for live count
    const interval = setInterval(syncGlobalVisitorStats, 60000);

    return () => {
      listeners.delete(setStats);
      clearInterval(interval);
    };
  }, []);

  const formattedTotal = Number(totalVisits).toLocaleString('en-IN');
  const formattedToday = Number(todayVisits).toLocaleString('en-IN');

  const labels = {
    en: { 
      label: 'Total Visitors', 
      short: 'Visitors',
      today: "Today's Visitors", 
      allTime: 'All-Time Visitors',
      status: 'Verified Counter',
      footer: 'Session-Verified MSME Analytics'
    },
    ta: { 
      label: 'மொத்த பார்வையாளர்கள்', 
      short: 'பார்வையாளர்கள்',
      today: 'இன்றைய பார்வையாளர்கள்', 
      allTime: 'மொத்த வருகை',
      status: 'சரிபார்க்கப்பட்ட எண்ணிக்கை',
      footer: 'அமர்வு சரிபார்க்கப்பட்ட தகவல்'
    },
    ml: { 
      label: 'ആകെ സന്ദർശകർ', 
      short: 'സന്ദർശകർ',
      today: 'ഇന്നത്തെ സന്ദർശകർ', 
      allTime: 'ആകെ സന്ദർശനങ്ങൾ',
      status: 'സ്ഥിരീകരിച്ച കൗണ്ടർ',
      footer: 'സെഷൻ സ്ഥിരീകരിച്ച വിശകലനം'
    },
    hi: { 
      label: 'कुल आगंतुक', 
      short: 'आगंतुक',
      today: 'आज के आगंतुक', 
      allTime: 'कुल विज़िट',
      status: 'सत्यापित गणना',
      footer: 'सत्र-सत्यापित एमएसएमई एनालिटिक्स'
    },
  }[lang] || { 
    label: 'Total Visitors', 
    short: 'Visitors',
    today: "Today's Visitors", 
    allTime: 'All-Time Visitors',
    status: 'Verified Counter',
    footer: 'Session-Verified MSME Analytics'
  };

  return (
    <div 
      className="visitor-counter-wrapper"
      role="status"
      tabIndex={0}
      aria-label={`${formattedTotal} total visitors`}
    >
      <div className="visitor-pill-btn" title={`${formattedTotal} Total Platform Visitors`}>
        <span className="live-beacon">
          <span className="live-dot" />
          <span className="live-wave" />
        </span>
        <div className="visitor-text-group">
          <span className="visitor-label">{labels.label}</span>
          <span className="visitor-odometer">{formattedTotal}</span>
        </div>
      </div>

      {/* Executive Micro-Popover Card on Hover / Focus */}
      <div className="visitor-telemetry-popover">
        <div className="telemetry-popover-header">
          <div className="telemetry-status-badge">
            <IconGlobe width={13} height={13} style={{ color: '#2563eb' }} />
            <span>{labels.status}</span>
          </div>
          <span className="telemetry-live-chip">
            <IconTrending width={11} height={11} style={{ marginRight: 4, display: 'inline-block', verticalAlign: 'middle' }} />
            Live Sync
          </span>
        </div>

        <div className="telemetry-grid">
          <div className="telemetry-stat-card primary">
            <span className="stat-card-label">{labels.allTime}</span>
            <span className="stat-card-value">{formattedTotal}</span>
          </div>

          <div className="telemetry-stat-card secondary">
            <span className="stat-card-label">{labels.today}</span>
            <span className="stat-card-value text-emerald">+{formattedToday}</span>
          </div>
        </div>

        <div className="telemetry-footer">
          <span className="telemetry-privacy-note">{labels.footer}</span>
        </div>
      </div>
    </div>
  );
};

export default VisitorCounter;
